import { saveRecord } from "../api/connection";
import { Vec2, Vec3 } from "../models/SyntheticMap";
import { createVisual, Visual } from "../models/Visual";
import { ObjectHandler } from "../panels/worldpanelparts/UserEventer";
import { WorldRenderer } from "../panels/WorldRenderer";
import * as THREE from "three";
import { WREvents } from "../panels/WREvents";
import { copyToEuler, alignSite, copyVec3 } from "./visual-utils";
import { PhysXObject } from "../panels/worldpanelparts/physx/PhysXObject";
import EventEmitter from "events";
import { CollisionBox } from "../models/Model";
import { IAgent, VisualVelocity } from "./ModelBasedVisual";
import { get } from "http";
import { getCreds } from "../contexts/PageContext";

export enum VisualEvents {
  DataUpdated = "DataUpdated",
  ObjectUpdated = "ObjectUpdated",
}

export abstract class BaseVisual implements Visual, ObjectHandler {
  public renderOrder = 0;
  public id = '';
  public position = { x: 0, y: 0, z: 0 };
  public location = { lat: 0, lon: 0, alt: 0 };
  public rotation: Vec3 = { x: 0, y: 0, z: 0 };
  public type = '';
  public siteAnchorId = '';
  public orgId = '';
  public _id: string = '';
  public wr!: WorldRenderer;
  public obj!: THREE.Object3D;
  public inCameraPane?: Vec2;
  public collection = 'visuals';
  public eventer = new EventEmitter();
  public agent?: IAgent;
  velocity: VisualVelocity = {
    angular: new THREE.Vector3(),
    linear: new THREE.Vector3()
  };

  constructor() {
    // Initialization code for BaseVisual
    this.type = this.getType();
    this.on(VisualEvents.DataUpdated, () => {
      this.updateObj();
      this.sceneAdjust();
      this.wr.physXService.updateObjects([this]);
    });
  }

  public convertToLocal(point: THREE.Vector3): THREE.Vector3 {
    return this.obj.worldToLocal(point.clone());
  }

  public on(event: VisualEvents, listener: (...args: any[]) => void): this {
    this.eventer.on(event, listener);
    return this;
  }

  public off(event: VisualEvents, listener: (...args: any[]) => void): this {
    this.eventer.off(event, listener);
    return this;
  }

  public emit(event: VisualEvents, ...args: any[]): boolean {
    return this.eventer.emit(event, ...args);
  }

  public removeAllListeners(event?: VisualEvents): this {
    this.eventer.removeAllListeners(event);
    return this;
  }

  public onClick(): void {
    this.select();
  }

  public select(): void {
    this.wr.emitEvent(WREvents.VisualSelected, this);
  }

  public updateObj(): void { }

  public getPhysXObject(): PhysXObject {
    const p: PhysXObject = {
      _id: this._id,
      id: this.id,
      props: this.getActorProps(),
      type: this.getType(),
      position: { x: this.obj.position.x, y: this.obj.position.y, z: this.obj.position.z },
      rotation: { x: this.obj.rotation.x, y: this.obj.rotation.y, z: this.obj.rotation.z },
      boxes: this.getCollisionBoxes(),
    };
    return p;
  }

  protected getActorProps(): any {
    return {};
  }

  protected getCollisionBoxes(): CollisionBox[] {
    return [];
  }

  public getFrindlyName(): string {
    return this.id || "Unnamed Visual";
  }

  public abstract getType(): string;

  public createDataTemplate(): Visual {
    return createVisual(this._id);
  }

  public render(delta: number): void {
    // Default render method, can be overridden by subclasses
  }

  public read(data: Visual): void {
    Object.assign(this, data);
  }

  public async load(wr: WorldRenderer): Promise<void> {
    // Logic to load the visual, if needed
    this.wr = wr;
    this.obj = await this.buildObj();
    this.obj.userData.visual = this;
    this.obj.userData.handler = this; // Set the handler for interaction
    if (this.siteAnchorId) {
      const siteAnchor = wr.dataService.getSite(this.siteAnchorId);
      copyVec3(this.position, this.obj.position);
      copyToEuler(this.rotation, this.obj.rotation);
      siteAnchor.addVisual(this);
    } else {
      const tiler = wr.planetService;
      const point = tiler.getPoint(this.location);
      const normal = point.clone().normalize();
      this.obj.position.copy(point);
      alignSite(this.obj, normal);
      tiler.worldGroup.add(this.obj);
    }
    await this.onLoaded();
  }

  protected async onLoaded() { }

  public sceneAdjust() { }

  protected abstract buildObj(): Promise<THREE.Object3D>;

  public write(): Visual {
    const data = this.createDataTemplate();
    const anyData = data as any;
    const anyThis = this as any;
    for (const key of Object.keys(anyData)) {
      anyData[key] = anyThis[key];
    }
    data.type = this.getType();
    return data;
  }

  public getParentVisual(): BaseVisual | null {
    if (this.obj.parent) {
      return this.obj.parent.userData.visual as BaseVisual;
    }
    return null;
  }

  async save(): Promise<void> {
    const data = this.write();
    const { org} = getCreds();
    if (!org) {
      throw new Error("No org in credentials. Saving visual aborted.");
    }
    data.orgId = org!._id;
    const res = await saveRecord(this.collection, data);
    data._id = res._id;
    this.read(data);
    this.wr.emit(WREvents.VisualSaved, this);
  }

  public onMouseMove(point: THREE.Vector3, buttons: number) {

  }

  public focusCamera() {
    const oldCp = this.wr.getCameraPosition();
    const up = this.obj.getWorldDirection(new THREE.Vector3(0, 0, 1));
    const cp = {
      position: oldCp.position,
      target: this.obj.getWorldPosition(new THREE.Vector3()),
      up,
    };
    this.wr.setCameraPosition(cp);
  }

}


